import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import SectionHeading from "@/components/base/SectionHeading";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import Toast from "@/components/base/Toast";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/useToast";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getRelatedVideos, getVideoById } from "@/utils/video";

export default function DetailPage() {
  const { id } = useParams();
  const numericId = Number(id);
  const item = useMemo(() => getVideoById(numericId), [numericId]);
  const related = useMemo(() => (item ? getRelatedVideos(item, 6) : []), [item]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { message, show } = useToast();
  const [currentEpisode, setCurrentEpisode] = useState(1);

  useEffect(() => {
    setCurrentEpisode(1);
  }, [numericId]);

  usePageMeta(
    item ? `${item.title} - 91视频免费看` : "影视资源不存在 - 91视频免费看",
    item ? `${item.title}：${item.description.slice(0, 90)}` : undefined
  );

  if (!item) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-24">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">影视资源不存在</h1>
          <p className="mt-3 text-sm text-foreground-500 max-w-md">
            抱歉，你访问的影视作品可能已下架或链接有误。可以返回首页继续浏览其他精彩内容。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const listPath = item.type === "movie" ? "/movie" : "/tv";
  const listLabel = item.type === "movie" ? "返回电影列表" : "返回电视剧列表";

  const handleFavorite = () => {
    toggleFavorite(item.id);
    show(favorited ? "已取消收藏" : "已加入收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      show("链接已复制到剪贴板");
    } catch {
      show("复制失败，请手动复制地址栏链接");
    }
  };

  const infoRows = [
    { label: "类型", value: item.category },
    { label: "地区", value: item.region },
    { label: "年代", value: `${item.year} 年` },
    item.type === "movie"
      ? { label: "时长", value: `${item.duration} 分钟` }
      : { label: "集数", value: `${item.episodes} 集（${item.status}）` },
    { label: "导演", value: item.director },
    { label: "上映", value: item.releaseDate },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={item.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top blur-2xl scale-110 opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/80 to-primary-950/95"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-sm text-background-300 mb-6 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
            <Link to={listPath} className="hover:text-accent-400 transition-colors">
              {item.type === "movie" ? "电影" : "电视剧"}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
            <span className="text-white">{item.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-52 shrink-0 mx-auto md:mx-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-secondary-900">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 影视详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-white">
              <h1 className="font-heading text-2xl md:text-4xl font-bold">{item.title}</h1>

              <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
                <span className="px-2.5 py-1 rounded bg-accent-500 text-white font-semibold">
                  评分 {item.rating.toFixed(1)}
                </span>
                {item.type === "tv" && item.status && (
                  <span className="px-2.5 py-1 rounded bg-primary-600 text-white">{item.status}</span>
                )}
                <span className="px-2.5 py-1 rounded bg-white/10 text-background-100">{item.category}</span>
                <span className="px-2.5 py-1 rounded bg-white/10 text-background-100">{item.region}</span>
                <span className="px-2.5 py-1 rounded bg-white/10 text-background-100">{item.year}</span>
              </div>

              <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3">
                {item.description}
              </p>

              <dl className="mt-5 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-background-400 text-xs">导演</dt>
                  <dd className="mt-0.5 text-white">{item.director}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-background-400 text-xs">主演</dt>
                  <dd className="mt-0.5 text-white">{item.cast.join(" / ")}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="#player"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </a>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    favorited ? "bg-accent-500 text-white" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
                <Link
                  to={listPath}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-go-back-line"></i>
                  {listLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-10 space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section id="player">
              <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950 flex flex-col items-center justify-center text-center px-6">
                <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500 text-white mb-4">
                  <i className="ri-play-fill text-3xl md:text-4xl"></i>
                </span>
                <p className="text-white font-medium">
                  {item.type === "tv" ? `正在播放 第 ${currentEpisode} 集` : "正在播放"}
                </p>
                <p className="mt-2 text-xs md:text-sm text-background-400">
                  视频播放功能演示，待接入真实播放服务
                </p>
              </div>
            </section>

            {item.type === "tv" && item.episodes && (
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  剧集列表
                  <span className="ml-2 text-sm font-normal text-foreground-500">共 {item.episodes} 集</span>
                </h2>
                <EpisodeList total={item.episodes} current={currentEpisode} onChange={setCurrentEpisode} />
              </section>
            )}

            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{item.description}</p>
            </section>
          </div>

          <aside>
            <div className="rounded-lg bg-background-100 border border-background-200 p-5 lg:sticky lg:top-24">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">影片资料</h2>
              <dl className="space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3 text-sm">
                    <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-900">{row.value}</dd>
                  </div>
                ))}
                <div className="flex items-start gap-3 text-sm">
                  <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900">{item.cast.join("、")}</dd>
                </div>
              </dl>
              <div className="mt-5 pt-5 border-t border-background-200 space-y-2.5">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    favorited ? "bg-accent-500 text-white hover:bg-accent-600" : "bg-primary-500 text-white hover:bg-primary-600"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {favorited ? "已收藏，点击取消" : "收藏这部作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-background-50 border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-link"></i>
                  复制分享链接
                </button>
              </div>
            </div>
          </aside>
        </div>

        <section>
          <SectionHeading title="相关推荐" subtitle="根据类型与地区为你匹配的相似作品" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {related.map((relatedItem) => (
              <VideoCard key={relatedItem.id} item={relatedItem} showTypeBadge />
            ))}
          </div>
        </section>
      </div>

      <Toast message={message} />
    </div>
  );
}